/*
  Scripts specific to Rek-AI implementation. 
  https://docs.rek.ai/
*/

interface RekAiResult {
  title: string;
  url: string;
  h1: string;
}

interface RekAiPredictionItem {
  title: string;
  url: string;
}

interface RekAiPrediction {
  predictions: RekAiPredictionItem[];
  holder: Element | null;
}

// Quicksearchs
var quickSearchFieldIds = [
  "page-search",
  "page-search-tablet",
  "page-search-sticky",
  "page-search-dw",
];

// Other
var searchPageSearchFieldIds = ["search-query", "publications-search-query"];

/*
  Gets the search page action url for the form
*/
export const getFormActionUrl = (id: string) => {
  var searchForm = document.getElementById(id)?.closest("form");
  return searchForm?.getAttribute("action");
};

function getPosition(string: string, subString: string, index: number) {
  return string.split(subString, index).join(subString).length;
}

const getFormSubTree = (id: string) => {
  const searchFormAction = document
    .getElementById(id)
    ?.closest("form")
    ?.getAttribute("action");
  if (searchFormAction && searchFormAction.split("/").length <= 3) return "/";

  if (!searchFormAction) return "/";

  return searchFormAction.substring(0, getPosition(searchFormAction, "/", 2));
};

function normalizeUrl(url: string) {
  try {
    const u = new URL(url, location.origin);
    // Normalize to origin + pathname (ignore query/hash; strip trailing slash)
    const pathname = u.pathname.replace(/\/+$/, "");
    return `${u.origin}${pathname}`;
  } catch {
    return url;
  }
}

function buildOnPageLinkSet() {
  const anchors = Array.from(
    document.querySelectorAll<HTMLAnchorElement>("a[href]"),
  );
  return new Set(anchors.map((a) => normalizeUrl(a.href)));
}

function applyOnPageFilter(
  preds: RekAiPredictionItem[],
  params: {
    filter_exclude_onpagelinks?: boolean;
    filter_exclude_currentpage?: boolean;
  },
) {
  if (!params.filter_exclude_onpagelinks) return preds;

  const onPage = buildOnPageLinkSet();
  const current = normalizeUrl(location.href);

  return preds.filter((p) => {
    const n = normalizeUrl(p.url);
    if (params.filter_exclude_currentpage && n === current) return false;
    return !onPage.has(n);
  });
}
// Function for rendering predictions
function renderHTML(data: RekAiPrediction) {
  const rekaAiPredictionHolder = document.querySelector(".output");
  if (!rekaAiPredictionHolder) return;

  const noOfHitsToDisplay = parseInt(
    rekaAiPredictionHolder.getAttribute("data-nrofhits") || "5",
    10,
  );
  const isSubPageHero = document.querySelector(".sub-page-hero");

  let noAddedLinks = 0;

  // Iterate recommendations
  for (let i = 0; i < data.predictions.length; i++) {
    if (noOfHitsToDisplay <= noAddedLinks) break;

    let rekAiPredictionListItem = document.createElement("li");

    let rekAIPredictionListItemLink = document.createElement("a");
    rekAIPredictionListItemLink.setAttribute("href", data.predictions[i].url);
    rekAIPredictionListItemLink.innerText = data.predictions[i].title;

    let rekAiPredictionListItemIcon = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg",
    );
    rekAiPredictionListItemIcon.setAttribute("id", `rek-ai-icon--${i}`);

    rekAiPredictionListItemIcon.setAttribute("aria-hidden", "true");
    rekAiPredictionListItemIcon.setAttribute("width", "10");
    rekAiPredictionListItemIcon.setAttribute("height", "22");

    let rekAiPredictionListItemIconPath = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "path",
    );

    if (isSubPageHero) {
      rekAiPredictionListItemIcon.setAttribute("viewBox", "0 0 40 40");

      rekAIPredictionListItemLink.classList.add("content-link");
      rekAiPredictionListItemIcon.classList.add(
        "icon",
        "rekai-recommendations__link__icon",
      );

      rekAiPredictionListItemIconPath.classList.add("st0");
      rekAiPredictionListItemIconPath.setAttribute(
        "d",
        "M5,18.01h22.73l-9.44-9.45l2.64-2.64l11.44,11.44v0L35,20L20.92,34.08l-2.64-2.64l9.45-9.45H5V18.01z",
      );
      rekAiPredictionListItemIconPath.setAttribute("fill", "currentColor");

      rekAiPredictionListItemIcon.append(rekAiPredictionListItemIconPath);
    } else {
      rekAiPredictionListItemIcon.setAttribute("viewBox", "0 0 10 22");

      rekAIPredictionListItemLink.classList.add("rounded-link");
      rekAiPredictionListItemIcon.classList.add("icon", "rounded-link__icon");

      rekAiPredictionListItemIconPath.setAttribute(
        "d",
        "M0 0h2l8 11-8 11H0l8-11z",
      );
      rekAiPredictionListItemIconPath.setAttribute("fill", "currentColor");
      rekAiPredictionListItemIconPath.setAttribute("fill-rule", "evenodd");

      rekAiPredictionListItemIcon.append(rekAiPredictionListItemIconPath);
    }

    rekAIPredictionListItemLink.append(rekAiPredictionListItemIcon);
    rekAiPredictionListItem.append(rekAIPredictionListItemLink);

    rekaAiPredictionHolder.append(rekAiPredictionListItem);

    noAddedLinks++;
  }
}

// Function for rendering recommendations
function renderRecommendationsHTML(data: RekAiPrediction) {
  const rekaAiRecommendationsHolder = data.holder;
  if (!rekaAiRecommendationsHolder) return;

  if (data.predictions.length <= 0) return;

  const noOfHitsToDisplay = parseInt(
    rekaAiRecommendationsHolder.getAttribute("data-nrofhits") || "5",
    10,
  );

  rekaAiRecommendationsHolder.setAttribute("class", "rekai-recommendations");

  let rekAiRecommendationTitle = document.createElement("h2");
  rekAiRecommendationTitle.setAttribute(
    "class",
    "rekai-recommendations__title",
  );
  rekAiRecommendationTitle.innerText =
    rekaAiRecommendationsHolder.getAttribute("data-title") ||
    "Rekommendationer";
  let rekAiRecommendationList = document.createElement("ul");
  rekAiRecommendationList.setAttribute("class", "rekai-recommendations__list");

  let noAddedLinks = 0;

  // Iterate recommendations
  for (let i = 0; i < data.predictions.length; i++) {
    if (noOfHitsToDisplay <= noAddedLinks) break;

    let rekAiPredictionListItem = document.createElement("li");

    let rekAIPredictionListItemLink = document.createElement("a");
    rekAIPredictionListItemLink.setAttribute("href", data.predictions[i].url);

    let rekAIPredictionListItemLinkText = document.createElement("span");
    rekAIPredictionListItemLinkText.classList.add(
      "rekai-recommendations__link__text",
    );
    rekAIPredictionListItemLinkText.innerText = data.predictions[i].title;

    // rekAIPredictionListItemLink.innerText = data.predictions[i].title;

    let rekAiPredictionListItemIcon = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg",
    );
    rekAiPredictionListItemIcon.setAttribute("id", `rek-ai-icon--${i}`);

    rekAiPredictionListItemIcon.setAttribute("aria-hidden", "true");
    rekAiPredictionListItemIcon.setAttribute("width", "10");
    rekAiPredictionListItemIcon.setAttribute("height", "22");

    let rekAiPredictionListItemIconPath = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "path",
    );

    rekAiPredictionListItemIcon.setAttribute("viewBox", "0 0 40 40");

    rekAIPredictionListItemLink.classList.add("rekai-recommendations__link");
    rekAiPredictionListItemIcon.classList.add(
      "icon",
      "rekai-recommendations__link__icon",
    );

    rekAiPredictionListItemIconPath.classList.add("st0");
    rekAiPredictionListItemIconPath.setAttribute(
      "d",
      "M5,18.01h22.73l-9.44-9.45l2.64-2.64l11.44,11.44v0L35,20L20.92,34.08l-2.64-2.64l9.45-9.45H5V18.01z",
    );
    rekAiPredictionListItemIconPath.setAttribute("fill", "currentColor");

    rekAiPredictionListItemIcon.append(rekAiPredictionListItemIconPath);

    rekAIPredictionListItemLink.append(rekAIPredictionListItemLinkText);
    rekAIPredictionListItemLink.append(rekAiPredictionListItemIcon);
    rekAiPredictionListItem.append(rekAIPredictionListItemLink);
    rekAiRecommendationList.append(rekAiPredictionListItem);

    noAddedLinks++;
  }

  rekaAiRecommendationsHolder.append(rekAiRecommendationTitle);
  rekaAiRecommendationsHolder.append(rekAiRecommendationList);
}

/* 
  https://docs.rek.ai/advanced/allparameters/prediction-parameters/gettermfromqueryparameter
*/
const setupRekaiRecommendations = () => {
  const holder = document.querySelector("#rekai__recommendations");
  const excludedPaths = holder?.getAttribute("data-excludetree") || "";
  const desired = parseInt(holder?.getAttribute("data-nrofhits") || "10", 10);

  const params: any = {
    addcontent: true,
    filter_exclude_onpagelinks: true,
    filter_exclude_currentpage: true,
    excludetree: excludedPaths,
    gettermfromqueryparameter: "query",
  };

  // Mirror Rek.ai over-fetch behavior to keep N after filtering
  let nr = desired * 3; // compensate for on-page removal

  params.nrofhits = nr;

  window.__rekai?.predict({ params }, (data: RekAiPrediction) => {
    // Apply our own filter (since custom render bypasses Rek.ai’s internal filter)
    const filtered = applyOnPageFilter(data.predictions, params);

    // Render exactly `desired` items (if available)
    renderRecommendationsHTML({
      ...data,
      predictions: filtered.slice(0, desired),
      holder: holder,
    });
  });
};

/* 
  https://docs.rek.ai/advanced/allparameters/filter-parameters/filterexcludeonpagelinks
*/
const setupRekaiPrediction = () => {
  const holder = document.querySelector(".output");
  const excludedPats = holder?.getAttribute("data-excludetree") || "";
  const desired = parseInt(holder?.getAttribute("data-nrofhits") || "10", 10);

  const params: any = {
    addcontent: true,
    filter_exclude_onpagelinks: true,
    filter_exclude_currentpage: true,
    excludetree: excludedPats,
  };

  // Mirror Rek.ai over-fetch behavior to keep N after filtering
  let nr = desired * 3; // compensate for on-page removal

  params.nrofhits = nr;

  window.__rekai?.predict({ params }, (data: RekAiPrediction) => {
    // Apply our own filter (since custom render bypasses Rek.ai’s internal filter)
    const filtered = applyOnPageFilter(data.predictions, params);

    // Render exactly `desired` items (if available)
    renderHTML({ ...data, predictions: filtered.slice(0, desired) });
  });
};

/*
  Setup for rek AI autocomplete
*/
export const setupRekAI = () => {
  document.addEventListener("DOMContentLoaded", (e) => {
    var searchInputs = document.querySelectorAll('input[type="search"]');

    searchInputs.forEach((input) => {
      const id = input.getAttribute("id");
      if (!id) return;

      if (typeof window.rekai_autocomplete !== "function") {
        console.warn("rekai_autocomplete is not available on window");
        return;
      }

      const rekAutocomplete = window.rekai_autocomplete(
        `#${id}`,
        {
          debug: false,
          params: {
            subtree: getFormSubTree(id),
            addcontent: true,
            openOnFocus: true,
          },
        },
        [
          {
            templates: {
              suggestion: (suggestion: RekAiResult) => suggestion.h1,
              empty: () =>
                '<div class="rekai-suggestion-empty">Din sökning gav inga träffar</div>',
            },
          },
        ],
      );

      if (!rekAutocomplete) return;

      rekAutocomplete.on(
        "rekai_autocomplete:selected",
        (_event: Event, suggestion: RekAiResult, _dataset: number) => {
          if (quickSearchFieldIds.includes(id)) {
            const searchPageUrl = getFormActionUrl(id);
            if (!searchPageUrl) return;

            window.location.href = `${searchPageUrl}?query=${encodeURIComponent(
              suggestion.title?.toLowerCase() ?? "",
            )}`;
          } else if (searchPageSearchFieldIds.includes(id)) {
            if (!suggestion.url) return;
            window.location.href = suggestion.url;
          }
        },
      );
    });

    setupRekaiPrediction();
    setupRekaiRecommendations();
  });
};

setupRekAI();
